<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const emit = defineEmits(['converted']);

const store = useStore();
const { t } = useI18n();

const show = defineModel('show', { type: Boolean, default: false });

const defaultDueAt = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setMinutes(0, 0, 0);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const form = ref({
  title: '',
  ticket_type: 'technical_issue',
  team_id: null,
  assignee_id: null,
  priority: 'medium',
  due_at: defaultDueAt(),
  internal_description: '',
  notify_customer: true,
  notify_agent: false,
});

const ticketTypes = [
  'technical_issue', 'bug_report', 'feature_request', 'journal_setup',
  'production_request', 'xml_jats_request', 'doi_indexing_request',
  'billing_issue', 'general_follow_up', 'other',
];

const agents = computed(() => store.getters['agents/getVerifiedAgents'] || store.getters['agents/getAgents'] || []);
const isSubmitting = computed(() => store.getters['tickets/getUIFlags'].isConverting);

const resetForm = () => {
  form.value = {
    title: '',
    ticket_type: 'technical_issue',
    team_id: null,
    assignee_id: null,
    priority: 'medium',
    due_at: defaultDueAt(),
    internal_description: '',
    notify_customer: true,
    notify_agent: false,
  };
};

watch(show, async visible => {
  if (!visible) return;
  resetForm();
  if (!agents.value.length) {
    await store.dispatch('agents/get');
  }
});

onMounted(() => {
  if (!agents.value.length) store.dispatch('agents/get');
});

const submit = async () => {
  if (!form.value.title.trim()) return;
  if (form.value.notify_agent && !form.value.assignee_id) {
    useAlert(t('TICKETING.MODAL.NOTIFY_AGENT_REQUIRES_ASSIGNEE'));
    return;
  }
  try {
    const payload = {
      ...form.value,
      assignee_id: form.value.assignee_id || null,
    };
    await store.dispatch('tickets/convertConversation', {
      conversationId: props.conversationId,
      payload,
    });
    useAlert(t('TICKETING.MODAL.CREATED'));
    show.value = false;
    emit('converted');
  } catch (e) {
    useAlert(e?.message || 'Error');
  }
};
</script>

<template>
  <woot-modal :show="show" size="medium" :on-close="() => (show = false)">
    <div class="flex flex-col gap-2.5 p-4 w-full max-w-md">
      <h2 class="text-base font-medium text-n-slate-12 mb-1">
        {{ $t('TICKETING.MODAL.TITLE') }}
      </h2>

      <label class="flex flex-col gap-0.5 text-sm">
        <span class="text-n-slate-11">{{ $t('TICKETING.MODAL.TITLE_LABEL') }}</span>
        <input v-model="form.title" class="woot-input !py-1.5" type="text" required />
      </label>

      <div class="grid grid-cols-2 gap-2">
        <label class="flex flex-col gap-0.5 text-sm">
          <span class="text-n-slate-11">{{ $t('TICKETING.MODAL.TYPE_LABEL') }}</span>
          <select v-model="form.ticket_type" class="woot-input !py-1.5">
            <option v-for="type in ticketTypes" :key="type" :value="type">
              {{ $t(`TICKETING.TYPES.${type.toUpperCase()}`) }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-0.5 text-sm">
          <span class="text-n-slate-11">{{ $t('TICKETING.MODAL.PRIORITY_LABEL') }}</span>
          <select v-model="form.priority" class="woot-input !py-1.5">
            <option value="low">{{ $t('TICKETING.PRIORITIES.LOW') }}</option>
            <option value="medium">{{ $t('TICKETING.PRIORITIES.MEDIUM') }}</option>
            <option value="high">{{ $t('TICKETING.PRIORITIES.HIGH') }}</option>
            <option value="urgent">{{ $t('TICKETING.PRIORITIES.URGENT') }}</option>
          </select>
        </label>
      </div>

      <div class="grid grid-cols-2 gap-2">
        <label class="flex flex-col gap-0.5 text-sm">
          <span class="text-n-slate-11">{{ $t('TICKETING.MODAL.AGENT_LABEL') }}</span>
          <select v-model="form.assignee_id" class="woot-input !py-1.5">
            <option :value="null">{{ $t('TICKETING.MODAL.AGENT_UNASSIGNED') }}</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name || agent.available_name || agent.email }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-0.5 text-sm">
          <span class="text-n-slate-11">{{ $t('TICKETING.MODAL.DUE_DATE_LABEL') }}</span>
          <input v-model="form.due_at" class="woot-input !py-1.5" type="datetime-local" />
        </label>
      </div>

      <label class="flex flex-col gap-0.5 text-sm">
        <span class="text-n-slate-11">{{ $t('TICKETING.MODAL.INTERNAL_DESCRIPTION_LABEL') }}</span>
        <textarea v-model="form.internal_description" class="woot-input !py-1.5" rows="2" />
      </label>

      <div class="flex flex-col gap-1.5 pt-1 text-sm">
        <label class="flex items-center gap-2">
          <input v-model="form.notify_customer" type="checkbox" />
          <span>{{ $t('TICKETING.MODAL.NOTIFY_CUSTOMER') }}</span>
        </label>
        <label class="flex items-start gap-2">
          <input v-model="form.notify_agent" class="mt-0.5" type="checkbox" />
          <span>{{ $t('TICKETING.MODAL.NOTIFY_AGENT') }}</span>
        </label>
        <p v-if="form.notify_agent" class="text-xs text-n-slate-10 ps-6">
          {{ $t('TICKETING.MODAL.NOTIFY_AGENT_HINT') }}
        </p>
      </div>

      <div class="flex gap-2 justify-end pt-1">
        <ButtonV4 size="sm" variant="ghost" :label="$t('TICKETING.MODAL.CANCEL')" @click="show = false" />
        <ButtonV4 size="sm" :label="$t('TICKETING.MODAL.SUBMIT')" :is-loading="isSubmitting" @click="submit" />
      </div>
    </div>
  </woot-modal>
</template>
